// Diagnostic snapshots only: never log SDP, tokens, TURN passwords, user names or IPs.
export function safeIceUrl(value) {
  if (typeof value !== 'string') return undefined;
  const match = value.match(/^(stuns?|turns?):(?:\/\/)?(?:[^@\s]+@)?(\[[a-fA-F0-9:]+\]|[a-zA-Z0-9.-]+)(:\d+)?(?:\?transport=(udp|tcp))?$/);
  return match ? `${match[1]}:${match[2]}${match[3] || ''}${match[4] ? `?transport=${match[4]}` : ''}` : '[redacted URL]';
}

export function iceServerSummary(servers = []) {
  return servers.map(server => ({
    urls: (Array.isArray(server.urls) ? server.urls : [server.urls]).map(safeIceUrl),
    credentialsPresent: !!(server.username && server.credential)
  }));
}

export function errorSummary(error) {
  // Browser error messages can embed entire SDP/candidates. Keep structured fields.
  return { name: error?.name || 'Error', detail: error?.errorDetail, sdpLineNumber: error?.sdpLineNumber };
}

export function candidateSummary(candidate) {
  // Signaling carries plain RTCIceCandidateInit, which may only contain the SDP line.
  const fields = typeof candidate?.candidate === 'string' ? candidate.candidate.trim().split(/\s+/) : [];
  const typeIndex = fields.indexOf('typ');
  const type = candidate?.candidateType || candidate?.type || (typeIndex >= 0 ? fields[typeIndex + 1] : undefined);
  const protocol = candidate?.protocol || fields[2]?.toLowerCase();
  return {
    type: ['host', 'srflx', 'prflx', 'relay'].includes(type) ? type : 'unknown',
    protocol: ['udp', 'tcp'].includes(protocol) ? protocol : undefined,
    relayProtocol: ['udp', 'tcp', 'tls'].includes(candidate?.relayProtocol) ? candidate.relayProtocol : undefined,
    tcpType: ['active', 'passive', 'so'].includes(candidate?.tcpType) ? candidate.tcpType : undefined,
    url: safeIceUrl(candidate?.url)
  };
}

export function connectionSnapshot(pc, report) {
  const rows = [...report.values()];
  const transport = rows.find(row => row.type === 'transport' && row.selectedCandidatePairId);
  const pair = transport ? report.get(transport.selectedCandidatePairId) : rows.find(row => row.type === 'candidate-pair' && row.selected === true);
  const local = pair && report.get(pair.localCandidateId);
  const remote = pair && report.get(pair.remoteCandidateId);
  const route = !pair ? 'not-selected' : !local || !remote ? 'unknown' :
    local.candidateType === 'relay' || remote.candidateType === 'relay' ? 'TURN' : 'direct';
  return {
    connection: pc.connectionState,
    ice: pc.iceConnectionState,
    gathering: pc.iceGatheringState,
    signaling: pc.signalingState,
    route,
    dtls: transport?.dtlsState,
    local: local ? candidateSummary(local) : undefined,
    remote: remote ? candidateSummary(remote) : undefined,
    pair: pair ? {
      state: pair.state, nominated: pair.nominated,
      bytesSent: pair.bytesSent, bytesReceived: pair.bytesReceived,
      requestsSent: pair.requestsSent, responsesReceived: pair.responsesReceived,
      roundTripTimeMs: pair.currentRoundTripTime === undefined ? undefined : Math.round(pair.currentRoundTripTime * 1000)
    } : undefined,
    pairStates: rows.filter(row => row.type === 'candidate-pair').reduce((counts, row) => {
      counts[row.state] = (counts[row.state] || 0) + 1;
      return counts;
    }, {}),
    candidates: ['local-candidate', 'remote-candidate'].map(side => ({
      side, types: rows.filter(row => row.type === side).reduce((counts, row) => {
        const type = candidateSummary(row).type;
        counts[type] = (counts[type] || 0) + 1;
        return counts;
      }, {})
    })),
    media: rows.filter(row => row.type === 'inbound-rtp' || row.type === 'outbound-rtp').map(row => ({
      direction: row.type === 'inbound-rtp' ? 'in' : 'out', kind: row.kind || row.mediaType,
      bytes: row.bytesReceived ?? row.bytesSent,
      packets: row.packetsReceived ?? row.packetsSent,
      packetsLost: row.packetsLost, framesDecoded: row.framesDecoded, framesEncoded: row.framesEncoded,
      audioLevel: row.audioLevel
    }))
  };
}

export function createMeetLogger() {
  const started = Date.now();
  const trace = Math.random().toString(36).slice(2, 8);
  return (event, data = {}, level = 'info') => {
    // Serialize immediately so DevTools shows values at the time of the event.
    try { console[level](`[meet ${trace} +${Date.now() - started}ms] ${event} ${JSON.stringify(data)}`); }
    catch { /* Diagnostics must never interrupt a call. */ }
  };
}

export class MeetingDiagnostics {
  constructor(pc, log, peer) {
    this.pc = pc;
    this.log = (event, data = {}, level) => log(event, { peer, ...data }, level);
    this.listeners = [];
    this.lastStatusAt = 0;
    this.localCandidates = {};
    this.on('icecandidate', event => {
      if (!event.candidate) {
        this.log('ice.local.complete', { types: this.localCandidates });
        return;
      }
      const candidate = candidateSummary(event.candidate);
      this.localCandidates[candidate.type] = (this.localCandidates[candidate.type] || 0) + 1;
      this.log('ice.local.candidate', candidate);
    });
    this.on('icecandidateerror', event => {
      const hints = {
        701: 'STUN/TURN server unreachable for this lookup; other servers/interfaces may still work',
        401: 'TURN authentication rejected; check shared secret and credentials',
        438: 'TURN nonce expired', 486: 'TURN allocation quota reached', 508: 'TURN allocation capacity exhausted'
      };
      this.log('ice.server.error', { url: safeIceUrl(event.url), code: event.errorCode,
        hint: hints[event.errorCode] || 'STUN/TURN request failed' }, 'warn');
    });
    for (const event of ['connectionstatechange', 'iceconnectionstatechange', 'icegatheringstatechange', 'signalingstatechange']) {
      this.on(event, () => {
        if (event === 'icegatheringstatechange' && pc.iceGatheringState === 'gathering') this.localCandidates = {};
        this.log(`state.${event}`, this.states());
        if (event === 'icegatheringstatechange' && pc.iceGatheringState === 'complete' && !this.localCandidates.relay) {
          this.log('ice.no-local-relay', { hint: 'No local TURN candidate gathered; inspect configured URLs, server errors, credentials and firewall' }, 'warn');
        }
        void this.sample(true);
      });
    }
    this.on('track', event => this.log('media.remote.track', { kind: event.track.kind, muted: event.track.muted, readyState: event.track.readyState }));
    this.timer = setInterval(() => { void this.sample(); }, 5000);
  }
  states() {
    const pc = this.pc;
    return { connection: pc.connectionState, ice: pc.iceConnectionState, gathering: pc.iceGatheringState, signaling: pc.signalingState };
  }
  on(event, callback) {
    this.pc.addEventListener(event, callback);
    this.listeners.push([event, callback]);
  }
  async sample(force = false) {
    if (this.closed || this.busy) return;
    this.busy = true;
    try {
      const report = await this.pc.getStats();
      if (this.closed) return;
      const snapshot = connectionSnapshot(this.pc, report);
      const transport = [...report.values()].find(row => row.type === 'transport' && row.selectedCandidatePairId);
      const selection = JSON.stringify([transport?.selectedCandidatePairId, snapshot.route, snapshot.local, snapshot.remote]);
      if (selection !== this.selection) {
        this.selection = selection;
        this.log('route.selected', snapshot);
      }
      if (force || Date.now() - this.lastStatusAt >= (this.pc.connectionState === 'connected' ? 30000 : 10000)) {
        this.lastStatusAt = Date.now();
        this.log('connection.stats', snapshot);
      }
    } catch (error) {
      if (!this.closed && !this.statsErrorLogged) {
        this.statsErrorLogged = true;
        this.log('stats.error', errorSummary(error), 'warn');
      }
    } finally { this.busy = false; }
  }
  close() {
    this.closed = true;
    clearInterval(this.timer);
    for (const [event, callback] of this.listeners) this.pc.removeEventListener(event, callback);
    this.listeners = [];
  }
}
